import { Button, Card, Input, message, Popconfirm, Select, Spin, Table, Modal, Dropdown } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { getUserDetails, makeGetCall, makePostCall } from '../../utils/helper';
import './template-list.css';
import EditTemplate from './EditTemplate';
import AddTemplate from '../AddTemplate';
import DateFormatter from '../../components/DateFormatter';
import ErrorBoundary from '../../components/ErrorBoundary';

const { Search } = Input;
const { Option } = Select;

const TemplateList = () => {
  const [modalitiesList, setModalitiesList] = useState([]);
  const [bodyPartsList, setBodyPartsList] = useState([]);
  const [templatesList, setTemplatesList] = useState({ data: [], loading: false, error: null });
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState('');
  const [selectedModality, setSelectedModality] = useState(null);
  const [selectedBodyPart, setSelectedBodyPart] = useState(null);
  const [selectedActive, setSelectedActive] = useState('');

  // Modal states
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [addTemplateModalVisible, setAddTemplateModalVisible] = useState(false);

  const isHOD = getUserDetails()?.user_type === 'hod';

  const getTemplatesList = useCallback(() => {
    setTemplatesList({ loading: true, data: [], error: null });
    makePostCall('/template-list', {})
      .then(res => {
        const templatesData = res.data?.data || [];
        setTemplatesList({ data: templatesData, loading: false, error: null });
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || 'Failed to load templates';
        setTemplatesList({ data: [], loading: false, error: errorMessage });
        message.error(`Error loading templates: ${errorMessage}`);
      });
  }, []);

  const getModalitiesList = useCallback(() => {
    makeGetCall('/modalities')
      .then(res => {
        const modalitiesData = res.data?.data || [];
        setModalitiesList(modalitiesData);
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || 'Failed to load modalities';
        message.error(`Error loading modalities: ${errorMessage}`);
      });
  }, []);

  const getBodyPartsList = useCallback(() => {
    makeGetCall('/body-parts')
      .then(res => {
        const bodyPartsData = res.data?.data || [];
        setBodyPartsList(bodyPartsData);
      })
      .catch(e => {
        const errorMessage = e.response?.data?.message || e.message || 'Failed to load body parts';
        message.error(`Error loading body parts: ${errorMessage}`);
      });
  }, []);

  useEffect(() => {
    getModalitiesList();
    getBodyPartsList();
    getTemplatesList();
  }, [getModalitiesList, getBodyPartsList, getTemplatesList]);

  // Memoized filter function to prevent unnecessary recalculations
  const filteredTemplates = useMemo(() => {
    let filtered = templatesList.data;

    // Filter by search text (template name or display name)
    if (searchText) {
      filtered = filtered.filter(
        template =>
          template.rt_template_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          template.rt_display_name?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Filter by modality
    if (selectedModality) {
      filtered = filtered.filter(template => template.rt_modality === selectedModality);
    }

    // Filter by body part
    if (selectedBodyPart) {
      filtered = filtered.filter(template => template.rt_bodypart === selectedBodyPart);
    }

    //Filter by Active status
    if (selectedActive) {
      filtered = filtered.filter(template => template.rt_deleted === selectedActive);
    }

    return filtered;
  }, [templatesList.data, searchText, selectedModality, selectedBodyPart, selectedActive]);

  // Memoized body parts filtering based on selected modality
  const filteredBodyPartsList = useMemo(() => {
    if (!selectedModality) {
      // Show all unique body parts when no modality is selected
      return bodyPartsList.filter(
        (item, index, self) =>
          index === self.findIndex(bp => (bp.code || bp.value) === (item.code || item.value))
      );
    } else {
      // Filter body parts based on templates that exist for the selected modality
      const availableBodyParts = templatesList.data
        .filter(template => template.rt_modality === selectedModality)
        .map(template => template.rt_bodypart)
        .filter((value, index, self) => self.indexOf(value) === index); // unique values

      return bodyPartsList.filter(bp => {
        return bp.modality === selectedModality && availableBodyParts.includes(bp.code || bp.value);
      });
    }
  }, [selectedModality, bodyPartsList, templatesList.data]);

  // Reset body part selection when modality changes
  useEffect(() => {
    if (selectedModality) {
      setSelectedBodyPart('');
    }
  }, [selectedModality]);

  const editTemplate = useCallback(template => {
    setSelectedTemplate(template);
    setEditModalVisible(true);
  }, []);

  const previewTemplate = useCallback(template => {
    setSelectedTemplate(template);
    setPreviewModalVisible(true);
  }, []);

  const handleTemplateEditSubmit = useCallback(
    updatedData => {
      makePostCall('/update-template', {
        ...updatedData,
        rt_id: selectedTemplate.rt_id,
        user_id: getUserDetails().username,
      })
        .then(res => {
          if (res.data?.success) {
            message.success('Template updated successfully');
            setEditModalVisible(false);
            getTemplatesList();
          } else {
            const errorMessage = res.data?.message || 'Failed to update template';
            message.error(errorMessage);
          }
        })
        .catch(err => {
          const errorMessage =
            err.response?.data?.message || err.message || 'Failed to update template';
          message.error(errorMessage);
        });
    },
    [selectedTemplate, getTemplatesList]
  );

  const deleteTemplate = useCallback(
    (template, action)  => {
      const flag = template.rt_deleted === 'N' ? true : false;
      const msg = flag ? 'Template deleted successfully' : 'Template activated sucessfully';

      console.log(template.rt_deleted, 'active', template.rt_id);
      setDeleteInProgress(true);
      makePostCall('/delete-template', { rt_id: template.rt_id, action: action })
        .then(res => {
          console.log(res);
          if (res.data?.success) {
            message.success(msg);
            getTemplatesList();
          } else {
            const errorMessage = res.data?.message || 'Failed to update template';
            message.error(errorMessage);
          }
        })
        .catch(e => {
          const errorMessage =
            e.response?.data?.message || e.message || 'Failed to update template';
          message.error(errorMessage);
        })
        .finally(() => {
          setDeleteInProgress(false);
        });
    },
    [getTemplatesList]
  );

  const clearFilters = useCallback(() => {
    setSearchText('');
    setSelectedModality('');
    setSelectedBodyPart('');
    setSelectedActive('');
  }, []);

  const getModalityLabel = useCallback(
    code => {
      if (!code) return 'N/A';
      const modality = modalitiesList.find(m => m.code === code || m.value === code);
      return modality ? modality.label : code;
    },
    [modalitiesList]
  );

  const getBodyPartLabel = useCallback(
    code => {
      if (!code) return 'N/A';
      const bodyPart = bodyPartsList.find(bp => bp.code === code || bp.value === code);
      return bodyPart ? bodyPart.label : code;
    },
    [bodyPartsList]
  );

  const TEMPLATE_COLUMNS = [
    {
      title: 'Template Name',
      key: 'template_name',
      render: (_, record) => record.rt_template_name || 'N/A',
    },
    {
      title: 'Display Name',
      key: 'display_name',
      render: (_, record) => record.rt_display_name || 'N/A',
    },
    {
      title: 'Modality',
      key: 'modality',
      render: (_, record) => getModalityLabel(record.rt_modality),
    },
    {
      title: 'Body Part',
      key: 'bodypart',
      render: (_, record) => getBodyPartLabel(record.rt_bodypart),
    },
    {
      title: 'Added By',
      key: 'added_by',
      render: (_, record) => record.rt_added_by || 'N/A',
    },
    {
      title: 'Added Date',
      key: 'added_dt',
      render: (_, record) => <DateFormatter date={record.rt_added_dt} />,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <span className={record.rt_deleted === 'N' ? 'status-active' : 'status-inactive'}>
          {record.rt_deleted === 'N' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <div className="action-buttons">
          <Button
            onClick={() => previewTemplate(record)}
            style={{ marginRight: '3px' }}
            title="Preview Template"
          >
            <EyeOutlined />
          </Button>
          <Button
            onClick={() => editTemplate(record)}
            style={{ marginRight: '3px' }}
            title="Edit Template"
            disabled={record.rt_deleted === 'Y'}
          >
            <EditOutlined />
          </Button>
         {record.rt_deleted === 'N' ? (
          <Popconfirm
            title="Delete template?"
            description="This action will update the template status."
            onConfirm={() => deleteTemplate(record, 'D')}
            okText="Yes"
            cancelText="No"
          >
              <Button title="Delete template?">
                  <DeleteOutlined />
              </Button>
          </Popconfirm>
          ) : (
          <>
          {/* Restore */}
          <Popconfirm
            title="Restore template?"
            description="This will make the template active again."
            onConfirm={() => deleteTemplate(record, 'A')}
            okText="Yes"
            cancelText="No"
          >
            <Button title="Restore template">
              <UndoOutlined />
            </Button>
          </Popconfirm>

          {/* Permanent Delete */}
          <Popconfirm
            title="Delete permanently?"
            description="This action cannot be undone."
            onConfirm={() => deleteTemplate(record, 'X')}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button danger title="Delete permanently">
              <DeleteOutlined />
            </Button>
          </Popconfirm>
          </>
        )}
        </div>
      ),
    },
  ];

  return (
    <ErrorBoundary>
      <Spin
        spinning={templatesList?.loading || deleteInProgress}
        tip={deleteInProgress ? 'Deleting template...' : 'Loading templates...'}
      >
        <div className="templates-container admin-container">
          {templatesList.error && (
            <div className="error-container">
              <strong>Error:</strong> {templatesList.error}
              <Button
                type="link"
                size="small"
                onClick={getTemplatesList}
                className="retry-button"
              >
                Retry
              </Button>
            </div>
          )}
          <div>
            <Card
              bordered={false}
              style={{ width: '100%', margin: '1rem auto' }}
              className="admin-card"
            >
              {/* Title and Filter Controls in same row */}
              <div className="template-header admin-header">
                <div className="page-title admin-page-title">TEMPLATES</div>
                {isHOD && (
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={() => setAddTemplateModalVisible(true)}
                    style={{ marginRight: 16 }}
                  >
                    Add Template
                  </Button>
                )}
                <div className="template-filters-inline admin-filters-inline">
                  <Search
                    placeholder="Search by template name or display name"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    style={{ width: 300, marginRight: 16 }}
                    prefix={<SearchOutlined />}
                  />

                  <Select
                    placeholder="Filter by Modality"
                    value={selectedModality}
                    onChange={setSelectedModality}
                    style={{ width: 200, marginRight: 16 }}
                    allowClear
                  >
                    {modalitiesList.map(modality => (
                      <Option
                        key={modality.code || modality.value}
                        value={modality.code || modality.value}
                      >
                        {modality.label}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    placeholder={selectedModality ? 'Filter by Body Part' : 'Select modality first'}
                    value={selectedBodyPart}
                    onChange={setSelectedBodyPart}
                    style={{ width: 200, marginRight: 16 }}
                    allowClear
                    disabled={!selectedModality}
                    notFoundContent={
                      selectedModality
                        ? 'No body parts for selected modality'
                        : 'Select a modality first'
                    }
                  >
                    {filteredBodyPartsList.map(bodyPart => (
                      <Option
                        key={bodyPart.code || bodyPart.value}
                        value={bodyPart.code || bodyPart.value}
                      >
                        {bodyPart.label}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    placeholder="Filter by Status"
                    value={selectedActive}
                    onChange={setSelectedActive}
                    style={{ width: 200, marginRight: 16 }}
                    allowClear
                  >
                    <Option
                      key="All"
                      value=""
                    >
                      All
                    </Option>
                    <Option
                      key="Active"
                      value="N"
                    >
                      Active
                    </Option>
                    <Option
                      key="InActive"
                      value="Y"
                    >
                      InActive
                    </Option>
                  </Select>
                  <Button onClick={clearFilters}>Clear Filters</Button>
                </div>
              </div>

              <Table
                className="template-list-table admin-table"
                columns={TEMPLATE_COLUMNS}
                dataSource={filteredTemplates}
                rowKey={record => record.rt_id}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} templates`,
                }}
              />
            </Card>
          </div>
        </div>

        <Modal
          title="Add Template"
          open={addTemplateModalVisible}
          onCancel={() => setAddTemplateModalVisible(false)}
          footer={null}
          width={1100}
          destroyOnClose
        >
          <AddTemplate
            onSuccess={() => {
              setAddTemplateModalVisible(false);
              getTemplatesList();
            }}
          />
        </Modal>

        {/* Edit Template Modal */}
        <EditTemplate
          visible={editModalVisible}
          templateData={selectedTemplate}
          modalitiesList={modalitiesList}
          bodyPartsList={bodyPartsList}
          onCancel={() => setEditModalVisible(false)}
          onSubmit={handleTemplateEditSubmit}
        />

        {/* Preview Template Modal */}
        <Modal
          title="Template Preview"
          open={previewModalVisible}
          onCancel={() => setPreviewModalVisible(false)}
          footer={[
            <Button
              key="close"
              onClick={() => setPreviewModalVisible(false)}
            >
              Close
            </Button>,
          ]}
          width={800}
        >
          {selectedTemplate && (
            <div className="template-preview">
              <div className="preview-meta">
                <p>
                  <strong>Template Name:</strong> {selectedTemplate.rt_template_name}
                </p>
                <p>
                  <strong>Display Name:</strong> {selectedTemplate.rt_display_name}
                </p>
                <p>
                  <strong>Modality:</strong> {getModalityLabel(selectedTemplate.rt_modality)}
                </p>
                <p>
                  <strong>Body Part:</strong> {getBodyPartLabel(selectedTemplate.rt_bodypart)}
                </p>
              </div>
              <div className="preview-content">
                <h4>Template Content:</h4>
                <div
                  className="template-html-content"
                  dangerouslySetInnerHTML={{ __html: selectedTemplate.template_html }}
                />
              </div>
            </div>
          )}
        </Modal>
      </Spin>
    </ErrorBoundary>
  );
};

export default TemplateList;
